import React from 'react'
import cx from 'classnames'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { BtnV2, l } from '@manychat/manyui'

import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import { GoalEditorMode } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { goalTypes } from 'apps/aiAutomation/lib/goals'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { GoalStatus, GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'

import { useSelectedGoalData } from '../../hooks/useSelectedGoalData'
import sharedCm from '../EditorLayout/EditorLayout.module.css'

interface PublishManagerProps {
  isActive: boolean
  isBlocked: boolean
  type: GoalType
}

export const PublishManager = ({ isActive, isBlocked, type }: PublishManagerProps) => {
  const dispatch = useAppDispatch()

  const { mode, editorRef, changeMode, save } = useGoalEditorContext(type)

  const activeGoalsLimitReached = useAppSelector(
    AiAutomationEngagementGoalSelectors.getIsActiveGoalLimitReached,
  )
  const { selectedGoal } = useSelectedGoalData()
  const isUpdating = useAppSelector((state) => {
    if (!selectedGoal) {
      return
    }

    return AiAutomationEngagementGoalSelectors.getIsItemUpdating(state, selectedGoal.id)
  })

  const isPublished = selectedGoal ? selectedGoal.status === GoalStatus.ACTIVE : false
  const isEditing = mode === GoalEditorMode.EDIT || mode === GoalEditorMode.SAVING_BLOCKED
  const isHighlighted = isActive && !isBlocked

  const handleSave = async () => {
    if (!editorRef?.current) {
      return
    }

    const result = await editorRef.current.submit()

    if (!result.isValid) {
      return
    }

    save(result.data)
    changeMode(GoalEditorMode.READ)
    dispatch(AiAutomationLayoutActions.showConfirmChanged(false))
  }

  const handleGoLive = async () => {
    if (!editorRef?.current || !selectedGoal) {
      return
    }

    const result = await editorRef.current.submit()

    if (!result.isValid) {
      return
    }

    save({ ...result.data, status: GoalStatus.ACTIVE }, true)
    changeMode(GoalEditorMode.READ)
    dispatch(AiAutomationLayoutActions.showConfirmChanged(false))
  }

  return (
    <section className={cx(sharedCm.section, isHighlighted && sharedCm.section_highlighted)}>
      <div className={cx(isActive && 'd-flex align-center justify-between gap')}>
        <div>
          {!isBlocked && !isPublished && (
            <>
              <h4 className={cx('font-semibold', !isActive && 'text-secondary')}>
                {l.getString(goalTypes[type].goLiveText)}
              </h4>
              {isActive && (
                <p className="m-t-xxs text-secondary">
                  {l.getString(goalTypes[type].publishCallToAction)}
                </p>
              )}
            </>
          )}

          {isPublished && (
            <>
              <h4 className={cx('font-semibold', !isActive && 'text-secondary')}>
                {isEditing
                  ? l.translate('Update your changes')
                  : l.translate('It’s live and working!')}
              </h4>
              <p className="m-t-xxs text-secondary">
                {isEditing
                  ? l.translate('Your updates will be applied right away.')
                  : l.getString(goalTypes[type].publishLiveText)}
              </p>
            </>
          )}

          {isBlocked && (
            <>
              <h4 className="font-semibold text-disabled">
                {l.translate('This goal can’t go live')}
              </h4>
              <p className="m-t-xxs text-disabled">
                {l.translate('Stop another active goal to set this one live')}
              </p>
            </>
          )}
        </div>

        {isActive && mode !== GoalEditorMode.READ && isPublished && !isBlocked && (
          <BtnV2 onClick={handleSave} variant="ghost-primary" loading={isUpdating}>
            {l.translate('Update')}
          </BtnV2>
        )}

        {selectedGoal && !isPublished && isActive && !activeGoalsLimitReached && (
          <BtnV2 onClick={handleGoLive} variant="ghost-primary" loading={isUpdating}>
            {l.translate('Go Live')}
          </BtnV2>
        )}
      </div>
    </section>
  )
}
